import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_WHITE_LABEL_CONFIG, PRESET_BY_ID, SCHOOL_PRESETS, VIBE_CODE_CONFIG } from './presets';
import { FIXED_BRAND_ID, IS_WHITE_LABEL_PRODUCT, PRODUCT_MODE, type ProductMode } from './product';
import { tokensDaPaleta, MARCAS_COM_IDENTIDADE, TIPOGRAFIA, ARTE } from './identidade';
import { resolveFixedBrand } from './brands';
import type { SchoolPresetId, WhiteLabelConfig } from './types';

const STORAGE_KEY = 'adaptive-school-white-label-v1';

interface BrandingContextValue {
  productMode: ProductMode;
  isWhiteLabelProduct: boolean;
  /** true = marca vem do build (VITE_BRAND); o customizador não se aplica. */
  isBrandLocked: boolean;
  config: WhiteLabelConfig;
  presets: typeof SCHOOL_PRESETS;
  setConfig: (updater: (prev: WhiteLabelConfig) => WhiteLabelConfig) => void;
  patchConfig: (patch: Partial<WhiteLabelConfig>) => void;
  patchTerminology: (patch: Partial<WhiteLabelConfig['terminology']>) => void;
  applyPreset: (presetId: SchoolPresetId) => void;
  resetBranding: () => void;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

function loadStoredConfig(): WhiteLabelConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_WHITE_LABEL_CONFIG;
    const parsed = JSON.parse(raw) as WhiteLabelConfig;
    return {
      ...DEFAULT_WHITE_LABEL_CONFIG,
      ...parsed,
      terminology: {
        ...DEFAULT_WHITE_LABEL_CONFIG.terminology,
        ...parsed.terminology,
      },
      accentPalette: parsed.accentPalette ?? DEFAULT_WHITE_LABEL_CONFIG.accentPalette,
    };
  } catch {
    return DEFAULT_WHITE_LABEL_CONFIG;
  }
}

function capitalize(value: string) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  // Marca fixada no build vence tudo: numa instância de cliente a identidade é
  // do deploy, não do aparelho. Sem isso, cada um dos 30 celulares da turma
  // abriria com a marca padrão até alguém configurar aquele aparelho.
  const marcaFixa = resolveFixedBrand(FIXED_BRAND_ID);
  const marcaTravada = marcaFixa !== null;

  const [config, setConfigState] = useState<WhiteLabelConfig>(() => {
    if (marcaFixa) return marcaFixa;
    return IS_WHITE_LABEL_PRODUCT ? loadStoredConfig() : VIBE_CODE_CONFIG;
  });

  const setConfig = useCallback((updater: (prev: WhiteLabelConfig) => WhiteLabelConfig) => {
    if (marcaTravada || !IS_WHITE_LABEL_PRODUCT) return;
    setConfigState(updater);
  }, [marcaTravada]);

  useEffect(() => {
    // Com marca fixa não gravamos nada: o localStorage de um aparelho não pode
    // divergir da identidade do deploy.
    if (marcaTravada || !IS_WHITE_LABEL_PRODUCT) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  }, [config, marcaTravada]);

  useEffect(() => {
    const [c1, c2, c3, c4] = config.accentPalette;
    const root = document.documentElement;
    root.style.setProperty('--brand-accent-1', c1);
    root.style.setProperty('--brand-accent-2', c2);
    root.style.setProperty('--brand-accent-3', c3);
    root.style.setProperty('--brand-accent-4', c4);

    /* A IDENTIDADE DA MARCA PINTA O APP.
       Até aqui os quatro acentos eram injetados e IGNORADOS: quem pintava a tela
       eram os tokens do `index.css`, cujo bloco `.dark` é comentado no próprio
       arquivo como "12brain identity". Por isso o telão parecia de outro produto
       — era a única tela lendo a paleta, escrita à mão no componente.

       Só marca DECLARADA recebe. O 12brain (sem VITE_BRAND) não entra aqui e
       continua idêntico — ele é a testemunha de que o escopo não vazou.

       Estilo inline no <html> ganha do `:root` e do `.dark` do CSS, então a
       identidade vale nos dois temas. Para marca fixa isso é intencional: a
       Globall é navy, não "navy quando o sistema estiver escuro". */
    if (!marcaTravada || !MARCAS_COM_IDENTIDADE.has(FIXED_BRAND_ID ?? '')) return;
    const tokens = tokensDaPaleta(config.accentPalette);
    for (const [nome, valor] of Object.entries(tokens)) {
      root.style.setProperty(nome, valor);
    }

    /* A TIPOGRAFIA VEM JUNTO, e a fonte é carregada SÓ AQUI.
       Importar no `main.tsx` colocaria ~180 KB de Archivo e Barlow no bundle do
       12brain, que não usa nenhuma das duas. Import dinâmico deixa o custo com
       quem consome. */
    const tipos = TIPOGRAFIA[FIXED_BRAND_ID ?? ''];
    if (tipos) {
      for (const [nome, valor] of Object.entries(tipos)) {
        root.style.setProperty(nome, valor);
      }
      void import('@fontsource-variable/archivo');
      void import('@fontsource/barlow/400.css');
      void import('@fontsource/barlow/500.css');
      void import('@fontsource/barlow/600.css');
      void import('@fontsource/barlow/700.css');
    }
  }, [config.accentPalette, marcaTravada]);

  useEffect(() => {
    document.title = config.brandName;
  }, [config.brandName]);

  /* O ÍCONE DA ABA também era do 12brain em todo host.
     `favicon.ico`, `apple-touch-icon` e `og:image` são arquivos estáticos do
     index.html — no host da Globall a aba mostrava a marca de outro produto.

     Sem arte da Globall no repo, o ícone sai do `logoEmoji` que a própria
     config declara (🌐, "o logo traz um globo no primeiro o de Globall"). É
     placeholder assumido, não invenção: quando o favicon oficial chegar,
     troca-se por um arquivo e esta função sai. */
  useEffect(() => {
    const arte = marcaTravada ? ARTE[FIXED_BRAND_ID ?? ''] : undefined;
    if (!arte) return;
    document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')
      .forEach((l) => { l.href = arte.favicon; });
    const touch = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    if (touch) touch.href = arte.touchIcon;
    const og = document.querySelector<HTMLMetaElement>('meta[property="og:image"]');
    if (og) og.content = arte.touchIcon;
  }, [marcaTravada]);

  const patchConfig = useCallback((patch: Partial<WhiteLabelConfig>) => {
    if (!IS_WHITE_LABEL_PRODUCT) return;
    setConfigState((prev) => ({ ...prev, ...patch }));
  }, []);

  const patchTerminology = useCallback((patch: Partial<WhiteLabelConfig['terminology']>) => {
    if (!IS_WHITE_LABEL_PRODUCT) return;
    setConfigState((prev) => ({
      ...prev,
      terminology: {
        ...prev.terminology,
        ...patch,
      },
    }));
  }, []);

  const applyPreset = useCallback((presetId: SchoolPresetId) => {
    if (!IS_WHITE_LABEL_PRODUCT) return;
    const preset = PRESET_BY_ID[presetId];
    if (!preset) return;
    setConfigState({
      ...preset.config,
      presetId,
    });
  }, []);

  const resetBranding = useCallback(() => {
    if (!IS_WHITE_LABEL_PRODUCT) return;
    setConfigState(DEFAULT_WHITE_LABEL_CONFIG);
  }, []);

  const value = useMemo<BrandingContextValue>(
    () => ({
      productMode: PRODUCT_MODE,
      isWhiteLabelProduct: IS_WHITE_LABEL_PRODUCT,
      isBrandLocked: marcaTravada,
      config,
      presets: SCHOOL_PRESETS,
      setConfig,
      patchConfig,
      patchTerminology,
      applyPreset,
      resetBranding,
    }),
    [config, patchConfig, patchTerminology, applyPreset, resetBranding],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used inside BrandingProvider');
  }

  const { config } = context;
  return {
    ...context,
    labels: {
      learnerSingular: config.terminology.learnerSingular,
      learnerPlural: config.terminology.learnerPlural,
      learnerSingularTitle: capitalize(config.terminology.learnerSingular),
      learnerPluralTitle: capitalize(config.terminology.learnerPlural),
      lessonSingular: config.terminology.lessonSingular,
      lessonPlural: config.terminology.lessonPlural,
      lessonSingularTitle: capitalize(config.terminology.lessonSingular),
      lessonPluralTitle: capitalize(config.terminology.lessonPlural),
      moduleSingular: config.terminology.moduleSingular,
      modulePlural: config.terminology.modulePlural,
      moduleSingularTitle: capitalize(config.terminology.moduleSingular),
      modulePluralTitle: capitalize(config.terminology.modulePlural),
      missionSingular: config.terminology.missionSingular,
      missionPlural: config.terminology.missionPlural,
      missionSingularTitle: capitalize(config.terminology.missionSingular),
      missionPluralTitle: capitalize(config.terminology.missionPlural),
    },
  };
}
